import React, {
  ComponentType,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import DiscountProductContext from "./DiscountProductContext";

/**
 * TBT-FIX: monte le vrai `DiscountProductProvider` hors du chunk app.js eager.
 *
 * Le chunk est injecté par le loader web-performance après `app.js` ;
 * ce provider ne retarde plus le chargement via `runAfterLcp`.
 *
 * REMOUNT-FIX: `{children}` reste sous un Context.Provider stable ; le vrai
 * provider monte en sibling. Évite Fragment→Provider qui remountait tout
 * l’arbre (blink pleine page + reload images).
 */
type ProviderComponent = ComponentType<{ children: ReactNode }>;

type Ctx = React.ContextType<typeof DiscountProductContext>;

const inertValue: Ctx = {
  addNotice: () => {},
  removeNotice: () => {},
  notices: [],
  isLoading: false,
  kxm: {
    data: [],
    isLoading: false,
    error: undefined,
  },
};

const DiscountProductDeferredProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [Real, setReal] = useState<ProviderComponent | null>(null);
  const [value, setValue] = useState<Ctx>(inertValue);

  useEffect(() => {
    if (Real) return;
    let cancelled = false;
    void import(
      /* webpackChunkName: "discount-product-provider" */ "./DiscountProductProvider"
    ).then((mod) => {
      if (cancelled) return;
      setReal(() => mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, [Real]);

  return (
    <>
      <DiscountProductContext.Provider value={value}>
        {children}
      </DiscountProductContext.Provider>
      {Real ? (
        <Real>
          <ContextValueMirror onValue={setValue} />
        </Real>
      ) : null}
    </>
  );
};

function ContextValueMirror({ onValue }: { onValue: (value: Ctx) => void }) {
  const ctx = useContext(DiscountProductContext);
  const prev = useRef<Ctx | null>(null);
  useEffect(() => {
    const p = prev.current;
    if (
      p &&
      p.notices === ctx.notices &&
      p.isLoading === ctx.isLoading &&
      p.discountProductOffered === ctx.discountProductOffered &&
      p.kxm.data === ctx.kxm.data &&
      p.kxm.isLoading === ctx.kxm.isLoading &&
      p.kxm.error === ctx.kxm.error &&
      p.addNotice === ctx.addNotice &&
      p.removeNotice === ctx.removeNotice
    ) {
      return;
    }
    prev.current = ctx;
    onValue(ctx);
  }, [ctx, onValue]);
  return null;
}

export default DiscountProductDeferredProvider;
